import { Box, Tooltip } from "@mui/material";
import Typography from "component/MarketPlace/Typography";
import EyeBrow from "component/MarketPlace/eyeBrow/eyeBrow";
import React, { useContext } from "react";
import ErrorIcon from "@mui/icons-material/Error";
import {
  ResourceType,
  TicketStatus,
} from "component/ticketComponents/ticketCard/types";
import { ThemeContext } from "styled-components";
import { mapColorEyeBrow } from "utils/mapColorEyeBrow";
import ContentDescription from "component/ticketComponents/ticketCard/ContentDescription";
import TimeUpdated from "component/ticketComponents/ticketCard/TimeUpated";
import GlobalSearchCard from "../GlobalSearchCard";
import DateUtils from "utils/date";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export interface ManageCardProps {
  onClickViewDetail: () => void;
  item: any;
  locale?: any;
  ticketType: string;
  tooltipStatusMessage?: any;
  createdDate: string;
  updatedDate: string;
}

const UNAVAIABLED = "N/A";

const ManageCard = (props: ManageCardProps) => {
  const {
    onClickViewDetail,
    item,
    locale,
    ticketType,
    tooltipStatusMessage,
    createdDate,
    updatedDate,
  } = props;
  const theme = useContext(ThemeContext);
  let state = item?.state;
  let requestItem = item?.requestItem;
  let approver = item?.approvalBy;
  const assetTicketLocale: { [key: string]: string } = {...locale?.asset, ...locale?.ticket};
  const requester =
    ticketType === ResourceType.Approvals
      ? (requestItem?.variables?.json_storage?.requestor_name || requestItem?.openBy)
      : ResourceType.Incidents === ticketType
      ? item?.requestFor
      : item?.openBy;
  const isCheckSubStage: boolean =
    (ResourceType.Requests === ticketType &&
      item?.variables?.stage?.display_value) ||
    (ResourceType.Approvals === ticketType && item?.originalState) ||
    (ResourceType.Incidents === ticketType && state);
  const renderOriginalState = (requestType: string, isMapColor: boolean) => {
    let text;
    switch (requestType) {
      case ResourceType.Approvals:
        text = item?.originalState;
        break;
      case ResourceType.Incidents:
        text = item?.state?.toUpperCase() === "OPEN" ? "SUBMITTED" : "RESOLVED";
        break;
      case ResourceType.Requests:
        text = isCheckSubStage
          ? item?.variables?.stage?.display_value
          : state;
        break;
      default:
        text = "";
        break;
    }
    return isMapColor
      ? text?.toUpperCase()
      : locale?.ticket?.[text?.toUpperCase()]?.toUpperCase() || text?.toUpperCase();
  };

  const eyeBrowOriginalStateColor = mapColorEyeBrow(
    renderOriginalState(ticketType, true),
    theme
  );

  const renderState = (requestType: string, isMapColor: boolean) => {
    let text;
    switch (requestType) {
      case ResourceType.Approvals:
        text = isMapColor
          ? state?.toUpperCase()
          : assetTicketLocale[state?.toUpperCase()]?.toUpperCase() +
            (item?.approver?.delegate ? ` - ${locale?.delegated}` : "");
        break;
      case ResourceType.Incidents:
        text = isMapColor
          ? state?.toUpperCase() === "RESOLVED"
            ? "OPEN"
            : state
          : state?.toUpperCase() === "RESOLVED"
          ? assetTicketLocale["OPEN"]?.toUpperCase()
          : assetTicketLocale[state?.toUpperCase()]?.toUpperCase();
        break;
      case ResourceType.Requests:
        text = isMapColor ? state?.toUpperCase() : assetTicketLocale[state?.toUpperCase()]?.toUpperCase();
        break;
      default:
        text = "";
        break;
    }
    return text?.toUpperCase();
  };

  const eyeBrowStateColor = mapColorEyeBrow(
    renderState(ticketType, true),
    theme
  );

  const tooltipMessage = (status: string) => {
    const tooltipStatusMessages: Record<string, string> = {
      [TicketStatus.OPEN]: tooltipStatusMessage?.open,
      [TicketStatus.CLOSED]: tooltipStatusMessage?.closed,
      [TicketStatus.AWAITING_APPROVAL]: tooltipStatusMessage?.awaitingApproval,
      [TicketStatus.PENDING_APPROVAL]: tooltipStatusMessage?.pendingApproval,
      [TicketStatus.APPROVED]: tooltipStatusMessage?.approved,
      [TicketStatus.REJECTED]: tooltipStatusMessage?.rejected,
      [TicketStatus.PREPARING_DEVICE]: tooltipStatusMessage?.preparingDevice,
      [TicketStatus.CANCELLED]: tooltipStatusMessage?.cancelled,
      [TicketStatus.RESOLVED]: tooltipStatusMessage?.resolved,
      [TicketStatus.DEVICEINTRANSIT]: tooltipStatusMessage?.deviceInTransit,
      [TicketStatus.INPROGRESS]: tooltipStatusMessage?.inProgress,
      [TicketStatus.NOLONGERREQUIRED]: tooltipStatusMessage?.noLongerRequired,
      [TicketStatus.PENDINGCANCELLATION]: tooltipStatusMessage?.pendingCancellation,
      [TicketStatus.SUBMITTED]: tooltipStatusMessage?.submitted,
      [TicketStatus.UNDERINVESTIGATION]: tooltipStatusMessage?.underInvestigation,
      [TicketStatus.APPROVAL_REJECTED]: tooltipStatusMessage?.approvalRejected,
      [TicketStatus.PCRETURN]: tooltipStatusMessage?.pcReturn,
      [TicketStatus.ORDERING_DEVICE]: tooltipStatusMessage?.orderingDevice,
      [TicketStatus.PREPARING_SHIPPING_MATERIALS]: tooltipStatusMessage?.preparingShippingMaterials,
      [TicketStatus.OEM_SUBMITTED]: tooltipStatusMessage?.oemSubmitted,
      [TicketStatus.OEM_COMPLETED]: tooltipStatusMessage?.oemCompleted,
      [TicketStatus.OEM_ACKNOWLEDGED]: tooltipStatusMessage?.oemAcknowledged,
      [TicketStatus.OEM_SHIPPED]: tooltipStatusMessage?.oemShipped,
      [TicketStatus.OEM_DELIVERED]: tooltipStatusMessage?.oemDelivered,
      [TicketStatus.SHIPPING]: tooltipStatusMessage?.shipping,
      [TicketStatus.COMPLETED]: tooltipStatusMessage?.completed,
      [TicketStatus.AWAITING_APPROVAL_DELEGATED]: tooltipStatusMessage?.awaitingApprovalDelegated,
      [TicketStatus.APPROVED_DELEGATED]: tooltipStatusMessage?.approvedDelegated,
      [TicketStatus.REJECTED_DELEGATED]: tooltipStatusMessage?.rejectedDelegated,
    };

    return tooltipStatusMessages[status] || "";
  };

  const mapBreadcrumb = (ticketType: string) => {
    const pathArray = ['home', 'manage', ticketType.slice(0, -1).toLowerCase()]; // remove 's' from ticketType
    return pathArray.map((path) => locale?.breadcrumb?.[path] || path).join(' / ');
  };

  return (
    <GlobalSearchCard
      title={ticketType === ResourceType.Approvals ? requestItem?.shortDescription : item?.shortDescription}
      breadcrumbs={mapBreadcrumb(ticketType)}
      onClickViewDetail={onClickViewDetail}
      locale={locale}
      paperProps={{ 'data-ritm_number': item?.number }}
    >
      <Box display="flex" justifyContent={{ xs: 'center', md: 'flex-start' }} alignItems="center" padding="10px 0">
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          minWidth={{ xs: 'auto', md: 'auto', lg: 'auto' }}
          maxWidth={{ xs: 'unset', md: '120px', lg: '160px' }}
        >
          <EyeBrow
            variant="filled"
            sx={{
              backgroundColor: eyeBrowStateColor?.background,
              color: eyeBrowStateColor?.color,
              fontWeight: 600,
              height: `${theme.uptimeSpace[12]}px`,
              lineHeight: `${theme.uptimeSpace[9]}px`,
              fontSize: `${theme.uptimeSpace[6]}px`,
            }}
            label={renderState(ticketType, false)}
            endIcon={
              tooltipMessage(renderState(ticketType, true)) ? (
                <Tooltip title={tooltipMessage(renderState(ticketType, true))}>
                  <Box display="flex">
                    <ErrorIcon
                      sx={{
                        color: '#0000008c',
                        width: `${theme.uptimeSpace[8]}px`,
                        height: `${theme.uptimeSpace[8]}px`,
                      }}
                    />
                  </Box>
                </Tooltip>
              ) : (
                <></>
              )
            }
            id="lbl-managecard-status"
            data-ritm_number={item?.requestItem?.number || item?.number}
          />
        </Box>
        {isCheckSubStage && (
          <>
            <ChevronRightIcon sx={{ width: { xs: '24px', sm: '60px' }, height: '24px' }} />
            <Box display="flex" justifyContent="flex-start" alignItems="center">
              <EyeBrow
                sx={{
                  backgroundColor: eyeBrowOriginalStateColor?.background,
                  padding: '3px 4px',
                  fontWeight: 600,
                  height: `${theme.uptimeSpace[12]}px`,
                  lineHeight: `${theme.uptimeSpace[9]}px`,
                  fontSize: `${theme.uptimeSpace[6]}px`,
                  color: eyeBrowOriginalStateColor?.color,
                }}
                label={renderOriginalState(ticketType, false)}
                variant="filled"
                endIcon={
                  tooltipMessage(renderOriginalState(ticketType, true)) ? (
                    <Tooltip title={tooltipMessage(renderOriginalState(ticketType, true))}>
                      <Box display="flex">
                        <ErrorIcon
                          sx={{
                            color: '#0000008c',
                            width: `${theme.uptimeSpace[8]}px`,
                            height: `${theme.uptimeSpace[8]}px`,
                          }}
                        />
                      </Box>
                    </Tooltip>
                  ) : (
                    <></>
                  )
                }
                id="lbl-managecard-status_ticket_type"
                data-ritm_number={item?.requestItem?.number || item?.number}
              />
            </Box>
          </>
        )}
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexDirection={{ xs: 'column', md: 'row' }}
        width="100%"
      >
        <Box display={{ xs: 'flex', md: 'none' }} justifyContent="flex-start" width={{ xs: "232px", sm: "262px", md: 'unset' }}>
          <Typography fontSize="14px" variant="overline" color="text.secondary">
            {ticketType === ResourceType.Approvals ? `#${item?.requestItem?.number}` : `#${item?.number}`}
          </Typography>          
        </Box>
        <Box display="flex" alignItems={{ xs: 'flex-start', md: 'center' }} flexDirection={{ xs: 'column', md: 'row' }}>
          <Box alignSelf="center" paddingRight="20px" display={{ xs: 'none', md: 'flex' }} width={{ xs: "232px", sm: "262px", md: 'unset' }}>
            <Typography fontSize="14px" variant="overline" color="text.secondary">
              {ticketType === ResourceType.Approvals ? `#${item?.requestItem?.number}` : `#${item?.number}`}
            </Typography>
          </Box>
          <ContentDescription label={locale?.requester} name={requester || UNAVAIABLED} />
          {approver?.name && ticketType !== ResourceType.Incidents && (
            <ContentDescription
              label={
                item?.originalState?.toUpperCase() === TicketStatus.REJECTED ||
                item?.variables?.stage?.display_value?.toUpperCase() === TicketStatus.REJECTED
                  ? locale?.rejectedBy
                  : locale?.approver
              }
              name={approver?.name || UNAVAIABLED}
            />
          )}
        </Box>
        <Box display="flex" alignItems={{ xs: 'flex-start', md: 'center' }} width={{ xs: "252px", sm: "282px", md: 'unset' }} flexDirection={{ xs: 'column', md: 'row' }}>
          <TimeUpdated label={locale?.created} date={DateUtils.formatDate(createdDate, 'll')} />
          {state?.toUpperCase() === TicketStatus.CLOSED && (
            <TimeUpdated label={locale?.closed} date={DateUtils.formatDate(updatedDate, 'll')} />
          )}
          <TimeUpdated label={locale?.updated} date={DateUtils.timetoXbyGMT(updatedDate)} />
        </Box>
      </Box>
    </GlobalSearchCard>
  );
};

export default ManageCard;
